import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { VesselSelect } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import { ReportTextCell, CrewDataTableFrame, CrewDataTablePrompt, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import type { PrevServiceExperienceResponse, PrevServiceExperienceRow } from './types';

const PrevServiceExperiencePage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const [vesselId, setVesselId] = useState('');
  const [appliedVesselId, setAppliedVesselId] = useState('');

  const listUrl = appliedVesselId
    ? `/crew/reports/previous-service?vesselId=${encodeURIComponent(appliedVesselId)}`
    : null;
  const { data, error, isLoading, mutate } = useSWR<PrevServiceExperienceResponse>(listUrl, fetcher, {
    revalidateOnFocus: false});

  const columns: Column<PrevServiceExperienceRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Crew',
        sortable: true,
        render: (_v, row) => (
          <Link to={`/crew/${row.crewId}`} state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline', colors.primaryFg)} onClick={(e) => e.stopPropagation()}>
            {row.name}
          </Link>
        )},
      { key: 'rank', header: 'Current rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'yearsInRank', header: 'Years in rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'yearsSameVesselType',
        header: 'Years same vessel type',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />},
      { key: 'yearsTotal', header: 'Years total', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'previousServiceCount',
        header: 'Prev. service rows',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />},
    ],
    [],
  );

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    setAppliedVesselId(vesselId);
  }, [vesselId]);

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hint={{
          title: 'Previous service / experience',
          body: 'Experience audit for onboard crew: years in current rank, on same vessel type, and total sea time from transactions plus previous service.'}}
        onSearch={handleSearch}
        searchDisabled={!vesselId}
        loading={isLoading}
        onRefresh={appliedVesselId ? () => mutate() : undefined}
        context={
          data?.vessel ? `${data.vessel.vesselName} · ${data.summary.crewCount} onboard crew` : undefined
        }
      >
        <CrewReportFilterField label="Vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            placeholder="Select vessel…"
            includeIds={vesselId ? [vesselId] : undefined}
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      <CrewDataTableFrame>
        {!appliedVesselId ? (
          <CrewDataTablePrompt>Select a vessel, then search.
          </CrewDataTablePrompt>
        ) : error ? (
          <CrewDataTablePrompt><span className="text-red-600">Could not load experience.</span></CrewDataTablePrompt>
        ) : (
          <DataTable
            data={data?.rows ?? []}
            columns={columns}
            keyExtractor={(row) => row.crewId}
            loading={isLoading}
            emptyMessage="No onboard crew"
            onRowClick={(row) => openCrew(row.crewId)}
            actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
            actionsPosition="left"
          />
        )}
      </CrewDataTableFrame>
    </div>
  );
};

export default PrevServiceExperiencePage;
